import type { SettingsPath } from "./settingsSearch";

export type SettingsSectionPath = Exclude<SettingsPath, "/settings/diagnostics">;

/** What this client can carry, independent of the Environment it is connected to. */
export interface SettingsClientBridges {
  readonly desktopBridgeAvailable: boolean;
  readonly connectBridgeAvailable: boolean;
}

export interface SettingsNavigationInput extends SettingsClientBridges {
  readonly admin: boolean;
  readonly managedByAfar: boolean;
}

export interface SettingsNavigationGroup {
  readonly label: string;
  readonly paths: ReadonlyArray<SettingsSectionPath>;
}

/**
 * Every settings section, grouped and in nav order. Account leads because it is the identity
 * every section below it depends on (#807).
 */
export const SETTINGS_NAVIGATION_GROUPS: ReadonlyArray<SettingsNavigationGroup> = [
  { label: "Account", paths: ["/settings/account", "/settings/admin"] },
  {
    label: "Workspace",
    paths: [
      "/settings/general",
      "/settings/projects",
      "/settings/source-control",
      "/settings/worktrees",
      "/settings/archived",
    ],
  },
  {
    label: "Agents",
    paths: ["/settings/providers", "/settings/mcp-servers", "/settings/integrations"],
  },
  {
    label: "App",
    paths: [
      "/settings/appearance",
      "/settings/keybindings",
      "/settings/snap-shot",
      "/settings/updates",
      "/settings/feedback",
    ],
  },
  {
    label: "Environments",
    paths: ["/settings/connections", "/settings/network", "/settings/organizations"],
  },
];

/**
 * Whether this client can show a section at all. The sidebar and each gated route's guard both
 * ask this, so a section is never listed where its route would redirect.
 *
 * - Account, Updates and Feedback need the Connect bridge. Signing out hides none of them:
 *   Feedback stays listed and explains that it needs an AFAR account, because a page that
 *   vanishes teaches nobody what it wanted (#808).
 * - Updates (Application Release checks, the Command line) exist only in the Electron shell.
 * - Network and Organizations belong to the Runtime Web Client alone: Desktop is a development
 *   surface and exposes none of AFAR Networks (ADR-0146).
 * - SnapShots capture windows in the Electron shell, and a Cloud Workspace's Screen and browser
 *   panels on any client that reaches one, which a browser does only through Connect (#1091).
 */
export function isSettingsSectionOfferedOnClient(
  path: SettingsSectionPath,
  bridges: SettingsClientBridges,
): boolean {
  switch (path) {
    case "/settings/account":
    case "/settings/feedback":
      return bridges.connectBridgeAvailable;
    case "/settings/updates":
      return bridges.connectBridgeAvailable && bridges.desktopBridgeAvailable;
    case "/settings/network":
    case "/settings/organizations":
      return bridges.connectBridgeAvailable && !bridges.desktopBridgeAvailable;
    case "/settings/snap-shot":
      return bridges.desktopBridgeAvailable || bridges.connectBridgeAvailable;
    default:
      return true;
  }
}

/**
 * Whether a section has anything to do on this client and Environment. Administration needs an
 * admin. A Cloud Workspace (`managedByAfar`) has no Providers section: AFAR runs its only Agent
 * Provider and clients cannot add, configure, or sign in to it (ADR-0130).
 */
function isSettingsSectionOffered(path: SettingsSectionPath, input: SettingsNavigationInput) {
  if (!isSettingsSectionOfferedOnClient(path, input)) return false;
  if (path === "/settings/admin") return input.admin;
  if (path === "/settings/providers") return !input.managedByAfar;
  return true;
}

/** The settings sections offered for the selected Environment, grouped; empty groups are dropped. */
export function resolveSettingsNavigationGroups(
  input: SettingsNavigationInput,
): ReadonlyArray<SettingsNavigationGroup> {
  return SETTINGS_NAVIGATION_GROUPS.map((group) => ({
    label: group.label,
    paths: group.paths.filter((path) => isSettingsSectionOffered(path, input)),
  })).filter((group) => group.paths.length > 0);
}

/** The offered sections as one list, in nav order. */
export function resolveSettingsNavigationPaths(
  input: SettingsNavigationInput,
): ReadonlyArray<SettingsSectionPath> {
  return resolveSettingsNavigationGroups(input).flatMap((group) => group.paths);
}
